import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import ItemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import { itemGroupsActionTypes } from "../../../store/actionTypes/items/categories/itemGroups/itemGroups.actionTypes";
import SelectInputElementComponent from "../../general/elements/inputElements/SelectInputElementComponent";
import AlertService from "../../../services/alerts/alert.service";
import { useSearchParams } from "react-router-dom";
import useItemGroupsQuery from "../../../hooks/queries/useItemGroupsQuery";
import { queryClient } from "../../../middlewares/cacheing/QueryClient.middleware";
import { SkeletonList } from "../../general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

import "../../../styles/components/dialogs/categories/connectionItemListWithCategoryDialogComponent.scss";

export const ConnectionItemListWithCategoryDialogComponent = ({ ItemGroupsActions, itemGroups, categories }) => {
  const [searchParams] = useSearchParams();
  const [selectedItemGroup, setSelectedItemGroup] = useState(null);
  const [category, setCategory] = useState();
  const categoryId = searchParams.get("id"); // Get the category ID from the query parameter

  const dispatch = useDispatch();
  const [localItemGroups, setLocalItemGroups] = useState([]);

  // 0016: the item-groups list is fetched once via React Query (deduped + cached) and bridged into
  // the itemGroupsReducer so this connected component keeps reading `itemGroups` from Redux
  // unchanged. The connect/disconnect/reorder mutations carry no list data, so each handler below
  // invalidates ["itemGroups"] → this mounted query refetches and re-bridges → the list updates
  // immediately (and a re-open within staleTime is fresh too).
  const itemGroupsQuery = useItemGroupsQuery({ getAll: true });
  const showSkeleton = useDelayedFlag(itemGroupsQuery.isLoading);

  useEffect(() => {
    if (itemGroupsQuery.data) {
      dispatch({ type: itemGroupsActionTypes.GET_ITEM_GROUPS_SUCCESS, data: itemGroupsQuery.data });
    }
  }, [itemGroupsQuery.data, dispatch]);

  useEffect(() => {
    setLocalItemGroups(itemGroups);
  }, [itemGroups]);

  useEffect(() => {
    if (categoryId) {
      const matchedCategory = categories?.find((category) => category.id === categoryId);
      setCategory(matchedCategory);
    }
  }, [categoryId, categories]);

  // 0016: refresh the list after a mutation by invalidating the React Query cache (the mounted
  // useItemGroupsQuery refetches and re-bridges into Redux).
  const refreshItemGroups = () => queryClient.invalidateQueries({ queryKey: ["itemGroups"] });

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSelectedItemGroup(value);
  };

  const handleConnectItemGroup = async () => {
    if (!selectedItemGroup || !categoryId) {
      AlertService.error("בחר קטגוריה וקבוצת פריטים כדי להמשיך");
      return;
    }

    try {
      await ItemGroupsActions.connectItemGroupWithCategory(categoryId, selectedItemGroup);
      AlertService.success("קבוצת הפריטים קושרה לקטגוריה בהצלחה");
      // Refresh the item groups list after a successful connection
      refreshItemGroups();
      setSelectedItemGroup(null);
    } catch (error) {
      AlertService.error("חלה שגיאה בקישור קבוצת הפריטים לקטגוריה");
      console.error("Error connecting item group with category:", error);
    }
  };

  const handleDisplayOrderChange = (itemGroupId, newOrder) => {
    // Update the local state for displayOrder
    const updatedItemGroups = localItemGroups.map((itemGroup) => {
      if (itemGroup.id === itemGroupId) {
        return { ...itemGroup, displayOrder: newOrder };
      }
      return itemGroup;
    });

    setLocalItemGroups(updatedItemGroups); // Update the local state
  };

  const handleUpdateDisplayOrder = async (itemGroupId, newOrder) => {
    try {
      await ItemGroupsActions.updateDisplayOrder(itemGroupId, newOrder);
      AlertService.success("סדר התצוגה עודכן בהצלחה");
      refreshItemGroups(); // Refresh the list after updating
    } catch (error) {
      AlertService.error("חלה שגיאה בעדכון סדר התצוגה");
      console.error("Error updating display order:", error);
    }
  };

  const getNotConnectedItemGroupsInputElement = () => {
    const itemGroupsOptions = itemGroups
      ?.filter((itemGroup) => !itemGroup.categoryId) // Only show item groups not connected to any category
      .map((itemGroup) => ({
        value: itemGroup.id,
        label: itemGroup.itemGroupName,
      }));

    const inputProps = {
      name: "itemGroups",
      value: selectedItemGroup,
      options: itemGroupsOptions,
      onChange: handleInputChange,
      searchable: true, // Enable search
    };
    return <SelectInputElementComponent props={inputProps} />;
  };

  const renderConnectedItemGroups = () => {
    const connectedItemGroups = localItemGroups?.filter((itemGroup) => itemGroup.categoryId === categoryId);

    return connectedItemGroups?.map((itemGroup) => (
      <div key={itemGroup.id} className="connectedItemGroup">
        <span>{itemGroup.itemGroupName}</span>
        <div className="actionsContainer">
          <input type="number" className="displayOrderInput" value={itemGroup.displayOrder || ""} onChange={(e) => handleDisplayOrderChange(itemGroup.id, parseInt(e.target.value, 10))} />
          <button className="secondaryButton updateButton" onClick={() => handleUpdateDisplayOrder(itemGroup.id, itemGroup.displayOrder)}>
            עדכן
          </button>
          <button className="secondaryButton deleteButton" onClick={() => handleDisconnectItemGroup(itemGroup.id)}>
            הסר קישור
          </button>
        </div>
      </div>
    ));
  };

  const handleDisconnectItemGroup = async (itemGroupId) => {
    try {
      await ItemGroupsActions.disconnectItemGroupFromCategory(categoryId, itemGroupId);
      AlertService.success("קבוצת הפריטים הוסרה מהקטגוריה בהצלחה");
      refreshItemGroups(); // Refresh the list after disconnecting
    } catch (error) {
      AlertService.error("חלה שגיאה בהסרת הקישור בין קבוצת הפריטים לקטגוריה");
      console.error("Error disconnecting item group from category:", error);
    }
  };

  return (
    <div className="dialogContainer ConnectionItemListWithCategoryContainer">
      <h2>קישור קבוצות פריטים לקטגוריה {category?.categoryName}</h2>
      <div className="ConnectItemListContainer">
        <label>בחר קבוצת פריטים לקישור</label>
        {getNotConnectedItemGroupsInputElement()}
        <button type="button" className="primaryButton" onClick={handleConnectItemGroup}>
          קשר קבוצת פריטים
        </button>
      </div>

      <div className="ConnectedItemListContainer">
        <h3>קבוצות פריטים מקושרות</h3>
        {itemGroupsQuery.isLoading ? showSkeleton && <SkeletonList rows={5} /> : renderConnectedItemGroups()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  itemGroups: state.itemGroupsReducer.itemGroups?.items, // Subscribe to item groups state from the reducer
  categories: state.categoryReducer.categories?.items, // Subscribe to categories state from the reducer
});

const mapDispatchToProps = (dispatch) => {
  return {
    ItemGroupsActions: bindActionCreators(ItemGroupsActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ConnectionItemListWithCategoryDialogComponent);
